import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  applyRollOver,
  getCreditUsageHistory,
  getFilteredAvailableCredits,
  getPreviousBillingCycle,
  getRollOverPreview,
  getRollOverList,
} from "../../utils/billing";
import { notify } from "../../utils/utils";

type BillingCycleState = {
  currentCycleEnd: string;
  currentCycleStart: string;
  cycleSource: string;
  previousCycleEnd: string;
  previousCycleStart: string;
};

const subtractDays = (value: string, days: number) => {
  const date = new Date(value);
  date.setDate(date.getDate() - days);
  return date.toISOString();
};

const pad = (value: number) => value.toString().padStart(2, "0");

const formatDateTime = (value?: string) => {
  if (!value) return "-";

  const date = new Date(value);

  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

export default function RollOver() {
  const { workspaceId } = useParams();
  const userData = useSelector((store: any) => store.globalReducer.userData);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [availableCredits, setAvailableCredits] = useState<number | null>(null);
  const [addHistory, setAddHistory] = useState<any[]>([]);
  const [rollOverBreakdown, setRollOverBreakdown] = useState<any[]>([]);
  const [billingCycle, setBillingCycle] = useState<BillingCycleState>({
    currentCycleEnd: "",
    currentCycleStart: "",
    cycleSource: "",
    previousCycleEnd: "",
    previousCycleStart: "",
  });
  const [loadingCycle, setLoadingCycle] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedAddBlockIds, setSelectedAddBlockIds] = useState<Set<string>>(
    new Set(),
  );
  const [selectedDeductRefBlockIds, setSelectedDeductRefBlockIds] = useState<
    Set<string>
  >(new Set());
  const [selectedReturnAddBlockIds, setSelectedReturnAddBlockIds] = useState<
    Set<string>
  >(new Set());
  const [showDeductRows, setShowDeductRows] = useState(false);
  const [showReturnRows, setShowReturnRows] = useState(false);
  const [showCreditHistory, setShowCreditHistory] = useState(false);
  const [selectedRollOverItem, setSelectedRollOverItem] = useState<any | null>(
    null,
  );
  const [applyingRollOver, setApplyingRollOver] = useState(false);
  const [rollOverList, setRollOverList] = useState<any[]>([]);
  const [loadingRollOverList, setLoadingRollOverList] = useState(false);
  const [previewContent, setPreviewContent] = useState<any>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState("");

  const previewRows = Array.isArray(previewContent?.processed)
    ? previewContent.processed
    : [];

  const appliedRollOverAddBlockIds = new Set(
    rollOverList
      .map((item: any) => item.addBlockId)
      .filter((addBlockId: string | undefined) => Boolean(addBlockId)),
  );

  const creditTypeTotals = addHistory.reduce(
    (acc: { add: number; deduct: number; return: number }, item: any) => {
      const amount = Number(item.amount) || 0;
      if (item.type === "add") acc.add += amount;
      if (item.type === "deduct") acc.deduct += amount;
      if (item.type === "return") acc.return += amount;
      return acc;
    },
    { add: 0, deduct: 0, return: 0 },
  );

  const allAddCredits = addHistory.filter((item: any) => item.type === "add");
  const allDeductRows = addHistory.filter(
    (item: any) => item.type === "deduct",
  );
  const allReturnRows = addHistory.filter(
    (item: any) => item.type === "return",
  );

  const addCreditsWithUsage = allAddCredits.map((item: any) => {
    const relatedDeductRows = allDeductRows.filter(
      (historyItem: any) => historyItem.refBlockId === item._id,
    );
    const relatedDeductRowIds = new Set(
      relatedDeductRows.map((historyItem: any) => historyItem._id),
    );
    const relatedReturnRows = allReturnRows.filter((historyItem: any) =>
      relatedDeductRowIds.has(historyItem.refBlockId),
    );

    const deductedAmount = relatedDeductRows.reduce(
      (total: number, historyItem: any) =>
        total + (Number(historyItem.amount) || 0),
      0,
    );
    const returnedAmount = relatedReturnRows.reduce(
      (total: number, historyItem: any) =>
        total + (Number(historyItem.amount) || 0),
      0,
    );

    return {
      ...item,
      deductedAmount,
      returnedAmount,
    };
  });

  const filteredAddCreditsWithUsage = addCreditsWithUsage.filter((item: any) =>
    selectedAddBlockIds.has(item._id),
  );

  const filteredAddCreditsTotal = filteredAddCreditsWithUsage.reduce(
    (total: number, item: any) => total + (Number(item.amount) || 0),
    0,
  );
  const filteredAddCreditsDeductTotal = filteredAddCreditsWithUsage.reduce(
    (total: number, item: any) => total + item.deductedAmount,
    0,
  );
  const filteredAddCreditsReturnTotal = filteredAddCreditsWithUsage.reduce(
    (total: number, item: any) => total + item.returnedAmount,
    0,
  );

  const uniqueDeductRefBlockIds = Array.from(
    new Set(allDeductRows.map((item: any) => item.refBlockId).filter(Boolean)),
  );

  const filteredDeductRows = allDeductRows.filter((item: any) =>
    selectedDeductRefBlockIds.has(item.refBlockId),
  );

  const filteredDeductTotal = filteredDeductRows.reduce(
    (total: number, item: any) => total + (Number(item.amount) || 0),
    0,
  );

  const deductIdToAddBlockId = allDeductRows.reduce(
    (map: Record<string, string>, item: any) => {
      if (item.refBlockId) map[item._id] = item.refBlockId;
      return map;
    },
    {},
  );

  const uniqueReturnAddBlockIds = Array.from(
    new Set(
      allReturnRows
        .map((item: any) => deductIdToAddBlockId[item.refBlockId])
        .filter(Boolean),
    ),
  );

  const filteredReturnRows = allReturnRows.filter((item: any) =>
    selectedReturnAddBlockIds.has(deductIdToAddBlockId[item.refBlockId]),
  );

  const filteredReturnTotal = filteredReturnRows.reduce(
    (total: number, item: any) => total + (Number(item.amount) || 0),
    0,
  );

  const addHistorySuffixSums = addHistory.reduceRight(
    (acc: number[], item: any) => {
      const prev = acc.length > 0 ? acc[0] : 0;
      const amount = Number(item.amount) || 0;
      const signed = item.type === "deduct" ? -amount : amount;
      return [prev + signed, ...acc];
    },
    [] as number[],
  );

  const fetchAddHistory = async () => {
    if (!workspaceId || !startDate) return;

    try {
      setLoadingHistory(true);
      const todayEnd = new Date();
      todayEnd.setHours(23, 59, 59, 999);
      const response = await getCreditUsageHistory(workspaceId, {
        types: "add,deduct,return",
        startDate,
        endDate: todayEnd.toISOString(),
      });
      const data = response.data || [];
      setAddHistory(data);
      setSelectedAddBlockIds(
        new Set(
          data.filter((i: any) => i.type === "add").map((i: any) => i._id),
        ),
      );
      setSelectedDeductRefBlockIds(
        new Set(
          data
            .filter((i: any) => i.type === "deduct" && i.refBlockId)
            .map((i: any) => i.refBlockId),
        ),
      );
      const deductMap: Record<string, string> = {};
      data
        .filter((i: any) => i.type === "deduct" && i.refBlockId)
        .forEach((i: any) => {
          deductMap[i._id] = i.refBlockId;
        });
      setSelectedReturnAddBlockIds(
        new Set(
          data
            .filter(
              (i: any) =>
                i.type === "return" && i.refBlockId && deductMap[i.refBlockId],
            )
            .map((i: any) => deductMap[i.refBlockId]),
        ),
      );
    } catch (error) {
      console.error("Error fetching rollover add history:", error);
      setAddHistory([]);
      setSelectedAddBlockIds(new Set());
      setSelectedDeductRefBlockIds(new Set());
      setSelectedReturnAddBlockIds(new Set());
    } finally {
      setLoadingHistory(false);
    }
  };
  const fetchRollOverSummary = async () => {
    if (!workspaceId || !startDate || !endDate) return;

    try {
      setLoadingSummary(true);
      const response = await getFilteredAvailableCredits(workspaceId, {
        startDate,
        endDate,
      });

      const addBlockMap = (response.debug?.addBlocks || []).reduce(
        (acc: Record<string, any>, block: any) => {
          acc[block._id] = block;
          return acc;
        },
        {},
      );

      setAvailableCredits(response.availableCredits ?? 0);
      setRollOverBreakdown(
        (response.debug?.breakdown || []).map((item: any) => ({
          ...item,
          createdAt: addBlockMap[item.addBlockId]?.createdAt || "",
          expiresAt: addBlockMap[item.addBlockId]?.expiresAt || "",
        })),
      );
    } catch (error) {
      console.error("Error fetching rollover summary:", error);
      setAvailableCredits(0);
      setRollOverBreakdown([]);
    } finally {
      setLoadingSummary(false);
    }
  };

  const previousBillingCycleText =
    billingCycle.previousCycleStart && billingCycle.previousCycleEnd
      ? `${formatDateTime(billingCycle.previousCycleStart)} to ${formatDateTime(
          billingCycle.previousCycleEnd,
        )}`
      : "";

  const currentBillingCycleText =
    billingCycle.currentCycleStart && billingCycle.currentCycleEnd
      ? `${formatDateTime(billingCycle.currentCycleStart)} to ${formatDateTime(
          billingCycle.currentCycleEnd,
        )}`
      : "";

  const handleApplyRollOver = async () => {
    if (!workspaceId || !selectedRollOverItem) {
      notify("Missing rollover selection.", "error");
      return;
    }

    if (!userData?._id) {
      notify("Missing logged-in user id.", "error");
      return;
    }

    try {
      setApplyingRollOver(true);
      const response = await applyRollOver(workspaceId, {
        addBlockId: selectedRollOverItem.addBlockId,
        rolloeverAmount: selectedRollOverItem.net ?? 0,
        triggeredByUserId: userData._id,
      });

      if (response?.success === false || response?.status?.code !== undefined && response.status.code !== 0) {
        throw new Error(response?.message || response?.status?.message || "Failed to apply rollover.");
      }

      notify("Rollover applied successfully.", "success");
      fetchRollOverSummary();
      fetchAddHistory();
      fetchRollOverList();
    } catch (error) {
      console.error("Error applying rollover:", error);
      notify(
        error?.message || error?.error || "Failed to apply rollover.",
        "error",
      );
    } finally {
      setApplyingRollOver(false);
      setSelectedRollOverItem(null);
    }
  };

  const fetchRollOverList = async () => {
    if (!workspaceId) return;
    try {
      setLoadingRollOverList(true);
      const response = await getRollOverList(workspaceId);
      setRollOverList(response.data || []);
    } catch (error) {
      console.error("Error fetching rollover list:", error);
      setRollOverList([]);
    } finally {
      setLoadingRollOverList(false);
    }
  };

  const fetchRollOverPreview = async () => {
    if (!workspaceId) return;

    try {
      setLoadingPreview(true);
      setPreviewError("");
      const response = await getRollOverPreview(workspaceId);
      setPreviewContent(response?.data ?? response ?? null);
    } catch (error: any) {
      console.error("Error fetching rollover preview:", error);
      setPreviewContent(null);
      setPreviewError(
        error?.message || error?.error || "Failed to load preview content.",
      );
    } finally {
      setLoadingPreview(false);
    }
  };

  useEffect(() => {
    fetchRollOverSummary();
  }, [workspaceId, startDate, endDate]);

  useEffect(() => {
    const fetchBillingCycle = async () => {
      if (!workspaceId) return;

      try {
        setLoadingCycle(true);
        const response = await getPreviousBillingCycle(workspaceId);
        const cycle = response.data;
        const adjustedPreviousCycleEnd = subtractDays(
          cycle.previousCycleEnd,
          1,
        );

        setStartDate(cycle.previousCycleStart || "");
        setEndDate(adjustedPreviousCycleEnd || "");
        setBillingCycle({
          currentCycleEnd: cycle.currentCycleEnd || "",
          currentCycleStart: cycle.currentCycleStart || "",
          cycleSource: cycle.source || "",
          previousCycleEnd: cycle.previousCycleEnd || "",
          previousCycleStart: cycle.previousCycleStart || "",
        });
      } catch (error) {
        console.error("Error fetching previous billing cycle:", error);
        setStartDate("");
        setEndDate("");
        setBillingCycle({
          currentCycleEnd: "",
          currentCycleStart: "",
          cycleSource: "",
          previousCycleEnd: "",
          previousCycleStart: "",
        });
      } finally {
        setLoadingCycle(false);
      }
    };

    fetchBillingCycle();
  }, [workspaceId]);

  useEffect(() => {
    fetchAddHistory();
  }, [workspaceId, startDate, endDate]);

  useEffect(() => {
    fetchRollOverList();
  }, [workspaceId]);

  useEffect(() => {
    fetchRollOverPreview();
  }, [workspaceId]);

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-[24px] font-semibold text-[#211f26]">Roll Over</h1>
        <p className="text-[14px] text-[#6f6c7a]">
          Workspace: {workspaceId || "-"}
        </p>
        <p className="text-[14px] text-[#6f6c7a]">
          Previous billing cycle:{" "}
          {loadingCycle
            ? "Loading..."
            : billingCycle.previousCycleStart && billingCycle.previousCycleEnd
              ? `${formatDateTime(billingCycle.previousCycleStart)} to ${formatDateTime(
                  billingCycle.previousCycleEnd,
                )}`
              : "-"}
        </p>
        <p className="text-[14px] text-[#6f6c7a]">
          Current billing cycle:{" "}
          {loadingCycle
            ? "Loading..."
            : billingCycle.currentCycleStart && billingCycle.currentCycleEnd
              ? `${formatDateTime(billingCycle.currentCycleStart)} to ${formatDateTime(
                  billingCycle.currentCycleEnd,
                )}`
              : "-"}
        </p>
        <p className="text-[13px] text-[#84828e]">
          Date format: dd-mm-yyyy hh:mm:ss
        </p>
      </div>

      <div className="rounded-2xl border border-[#eae7ec] bg-white p-5">
        <div className="mb-4 flex flex-col gap-3">
          <h2 className="text-[18px] font-semibold text-[#211f26]">
            Added Credits
          </h2>

          <div className="flex flex-wrap gap-2 rounded-xl border border-[#eae7ec] bg-[#faf9fb] p-3">
            {addCreditsWithUsage.map((item: any) => (
              <label
                key={item._id}
                className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-[#eae7ec] bg-white px-3 py-1.5 text-[12px]"
              >
                <input
                  type="checkbox"
                  checked={selectedAddBlockIds.has(item._id)}
                  onChange={(e) => {
                    const next = new Set(selectedAddBlockIds);
                    e.target.checked
                      ? next.add(item._id)
                      : next.delete(item._id);
                    setSelectedAddBlockIds(next);
                  }}
                />
                <span className="text-[#211f26]">{item._id}</span>
              </label>
            ))}
            {addCreditsWithUsage.length === 0 && (
              <span className="text-[12px] text-[#84828e]">
                No credit blocks available.
              </span>
            )}
          </div>

          <div className="flex gap-6 text-[12px] text-[#84828e]">
            <span>
              Total added:{" "}
              <span className="font-semibold text-[#211f26]">
                {loadingHistory ? "…" : filteredAddCreditsTotal}
              </span>
            </span>
            <span>
              Total deduct:{" "}
              <span className="font-semibold text-[#211f26]">
                {loadingHistory ? "…" : filteredAddCreditsDeductTotal}
              </span>
            </span>
            <span>
              Total return:{" "}
              <span className="font-semibold text-[#211f26]">
                {loadingHistory ? "…" : filteredAddCreditsReturnTotal}
              </span>
            </span>
            <span>
              Sum:{" "}
              <span className="font-semibold text-[#211f26]">
                {loadingHistory
                  ? "…"
                  : filteredAddCreditsTotal -
                    filteredAddCreditsDeductTotal +
                    filteredAddCreditsReturnTotal}
              </span>
            </span>
          </div>
        </div>

        <Table className="w-full">
          <TableHeader>
            <TableRow className="border-b-0">
              <TableHead className="bg-[#faf9fb]">S.No.</TableHead>
              <TableHead className="bg-[#faf9fb]">Credit Block ID</TableHead>
              <TableHead className="bg-[#faf9fb]">Amount</TableHead>
              <TableHead className="bg-[#faf9fb]">Amount Paid</TableHead>
              <TableHead className="bg-[#faf9fb]">Linked Deduct</TableHead>
              <TableHead className="bg-[#faf9fb]">Linked Return</TableHead>
              <TableHead className="bg-[#faf9fb]">Transaction ID</TableHead>
              <TableHead className="bg-[#faf9fb]">
                Transaction Group ID
              </TableHead>
              <TableHead className="bg-[#faf9fb]">Invoice ID</TableHead>
              <TableHead className="bg-[#faf9fb]">Created At</TableHead>
              <TableHead className="bg-[#faf9fb]">Expires At</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {filteredAddCreditsWithUsage.map((item: any, index: number) => (
              <TableRow key={item._id}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{item._id}</TableCell>
                <TableCell>{item.amount ?? "-"}</TableCell>
                <TableCell>{item.amountPaid ?? "-"}</TableCell>
                <TableCell>{item.deductedAmount}</TableCell>
                <TableCell>{item.returnedAmount}</TableCell>
                <TableCell>{item.transactionId || "-"}</TableCell>
                <TableCell>{item.transactionGroupId || "-"}</TableCell>
                <TableCell>{item.meta?.stripeInvoiceId || "-"}</TableCell>
                <TableCell>{formatDateTime(item.createdAt)}</TableCell>
                <TableCell>{formatDateTime(item.expiresAt)}</TableCell>
              </TableRow>
            ))}
            {filteredAddCreditsWithUsage.length > 0 && (
              <TableRow className="border-t-2 border-[#eae7ec] font-semibold">
                <TableCell colSpan={2}>Total</TableCell>
                <TableCell>{filteredAddCreditsTotal}</TableCell>
                <TableCell>-</TableCell>
                <TableCell>{filteredAddCreditsDeductTotal}</TableCell>
                <TableCell>{filteredAddCreditsReturnTotal}</TableCell>
                <TableCell colSpan={5} />
              </TableRow>
            )}
          </TableBody>
        </Table>

        {!loadingHistory && filteredAddCreditsWithUsage.length === 0 && (
          <div className="pt-4 text-[14px] text-[#84828e]">
            {selectedAddBlockIds.size === 0
              ? "No credit blocks selected."
              : "No added credits found."}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-[#eae7ec] bg-white p-5">
        <div className={`flex flex-col gap-3 ${showDeductRows ? "mb-4" : ""}`}>
          <div className="flex items-center justify-between">
            <h2 className="text-[18px] font-semibold text-[#211f26]">
              Deduct Rows
            </h2>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-[12px] text-[#84828e]">Total</p>
                <p className="text-[18px] font-semibold text-[#211f26]">
                  {filteredDeductTotal}
                </p>
              </div>
              <button
                className="rounded-lg border border-[#eae7ec] px-3 py-1.5 text-[13px] text-[#84828e] hover:bg-[#faf9fb]"
                onClick={() => setShowDeductRows((v) => !v)}
              >
                {showDeductRows ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          {showDeductRows && (
            <div className="flex flex-wrap gap-2 rounded-xl border border-[#eae7ec] bg-[#faf9fb] p-3">
              {uniqueDeductRefBlockIds.map((refId: string) => (
                <label
                  key={refId}
                  className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-[#eae7ec] bg-white px-3 py-1.5 text-[12px]"
                >
                  <input
                    type="checkbox"
                    checked={selectedDeductRefBlockIds.has(refId)}
                    onChange={(e) => {
                      const next = new Set(selectedDeductRefBlockIds);
                      e.target.checked ? next.add(refId) : next.delete(refId);
                      setSelectedDeductRefBlockIds(next);
                    }}
                  />
                  <span className="text-[#211f26]">{refId}</span>
                </label>
              ))}
              {uniqueDeductRefBlockIds.length === 0 && (
                <span className="text-[12px] text-[#84828e]">
                  No deduct rows available.
                </span>
              )}
            </div>
          )}
        </div>

        {showDeductRows && (
          <>
            <Table className="w-full">
              <TableHeader>
                <TableRow className="border-b-0">
                  <TableHead className="bg-[#faf9fb]">S.No.</TableHead>
                  <TableHead className="bg-[#faf9fb]">History Row ID</TableHead>
                  <TableHead className="bg-[#faf9fb]">
                    Reference Block ID
                  </TableHead>
                  <TableHead className="bg-[#faf9fb]">Amount</TableHead>
                  <TableHead className="bg-[#faf9fb]">Transaction ID</TableHead>
                  <TableHead className="bg-[#faf9fb]">
                    Transaction Group ID
                  </TableHead>
                  <TableHead className="bg-[#faf9fb]">Info</TableHead>
                  <TableHead className="bg-[#faf9fb]">Created At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDeductRows.map((item: any, index: number) => (
                  <TableRow key={item._id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{item._id}</TableCell>
                    <TableCell>{item.refBlockId || "-"}</TableCell>
                    <TableCell>{item.amount ?? "-"}</TableCell>
                    <TableCell>{item.transactionId || "-"}</TableCell>
                    <TableCell>{item.transactionGroupId || "-"}</TableCell>
                    <TableCell>{item.meta?.info || "-"}</TableCell>
                    <TableCell>{formatDateTime(item.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {!loadingHistory && filteredDeductRows.length === 0 && (
              <div className="pt-4 text-[14px] text-[#84828e]">
                {selectedDeductRefBlockIds.size === 0
                  ? "No reference block IDs selected."
                  : "No deduct rows found."}
              </div>
            )}
          </>
        )}
      </div>

      <div className="rounded-2xl border border-[#eae7ec] bg-white p-5">
        <div className={`flex flex-col gap-3 ${showReturnRows ? "mb-4" : ""}`}>
          <div className="flex items-center justify-between">
            <h2 className="text-[18px] font-semibold text-[#211f26]">
              Return Rows
            </h2>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-[12px] text-[#84828e]">Total</p>
                <p className="text-[18px] font-semibold text-[#211f26]">
                  {filteredReturnTotal}
                </p>
              </div>
              <button
                className="rounded-lg border border-[#eae7ec] px-3 py-1.5 text-[13px] text-[#84828e] hover:bg-[#faf9fb]"
                onClick={() => setShowReturnRows((v) => !v)}
              >
                {showReturnRows ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          {showReturnRows && (
            <div className="flex flex-wrap gap-2 rounded-xl border border-[#eae7ec] bg-[#faf9fb] p-3">
              {uniqueReturnAddBlockIds.map((addBlockId: string) => (
                <label
                  key={addBlockId}
                  className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-[#eae7ec] bg-white px-3 py-1.5 text-[12px]"
                >
                  <input
                    type="checkbox"
                    checked={selectedReturnAddBlockIds.has(addBlockId)}
                    onChange={(e) => {
                      const next = new Set(selectedReturnAddBlockIds);
                      e.target.checked
                        ? next.add(addBlockId)
                        : next.delete(addBlockId);
                      setSelectedReturnAddBlockIds(next);
                    }}
                  />
                  <span className="text-[#211f26]">{addBlockId}</span>
                </label>
              ))}
              {uniqueReturnAddBlockIds.length === 0 && (
                <span className="text-[12px] text-[#84828e]">
                  No add block IDs available.
                </span>
              )}
            </div>
          )}
        </div>

        {showReturnRows && (
          <>
            <Table className="w-full">
              <TableHeader>
                <TableRow className="border-b-0">
                  <TableHead className="bg-[#faf9fb]">S.No.</TableHead>
                  <TableHead className="bg-[#faf9fb]">History Row ID</TableHead>
                  <TableHead className="bg-[#faf9fb]">Add Block ID</TableHead>
                  <TableHead className="bg-[#faf9fb]">Ref Block ID</TableHead>
                  <TableHead className="bg-[#faf9fb]">Amount</TableHead>
                  <TableHead className="bg-[#faf9fb]">Transaction ID</TableHead>
                  <TableHead className="bg-[#faf9fb]">
                    Transaction Group ID
                  </TableHead>
                  <TableHead className="bg-[#faf9fb]">Info</TableHead>
                  <TableHead className="bg-[#faf9fb]">Created At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReturnRows.map((item: any, index: number) => (
                  <TableRow key={item._id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{item._id}</TableCell>
                    <TableCell>
                      {deductIdToAddBlockId[item.refBlockId] || "-"}
                    </TableCell>
                    <TableCell>{item.refBlockId || "-"}</TableCell>
                    <TableCell>{item.amount ?? "-"}</TableCell>
                    <TableCell>{item.transactionId || "-"}</TableCell>
                    <TableCell>{item.transactionGroupId || "-"}</TableCell>
                    <TableCell>{item.meta?.info || "-"}</TableCell>
                    <TableCell>{formatDateTime(item.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {!loadingHistory && filteredReturnRows.length === 0 && (
              <div className="pt-4 text-[14px] text-[#84828e]">
                {selectedReturnAddBlockIds.size === 0
                  ? "No add block IDs selected."
                  : "No return rows found."}
              </div>
            )}
          </>
        )}
      </div>

      <div className="rounded-2xl border border-[#eae7ec] bg-white p-5">
        <div
          className={`flex flex-col gap-4 md:flex-row md:items-end md:justify-between ${showCreditHistory ? "mb-4" : ""}`}
        >
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-[18px] font-semibold text-[#211f26]">
                Credit History
              </h2>
              <button
                className="rounded-lg border border-[#eae7ec] px-3 py-1.5 text-[13px] text-[#84828e] hover:bg-[#faf9fb]"
                onClick={() => setShowCreditHistory((v) => !v)}
              >
                {showCreditHistory ? "Hide" : "Show"}
              </button>
            </div>
            <p className="text-[13px] text-[#84828e]">
              Filtered by `types=add` for the previous billing cycle.
            </p>
            {billingCycle.cycleSource && (
              <p className="text-[12px] text-[#84828e]">
                Cycle source: {billingCycle.cycleSource}
              </p>
            )}
            <p className="text-[12px] text-[#84828e]">
              All table dates use: dd-mm-yyyy hh:mm:ss
            </p>
            <div className="mt-3 flex gap-4">
              <div className="rounded-xl border border-[#eae7ec] bg-[#faf9fb] px-4 py-3">
                <p className="text-[12px] text-[#84828e]">Sum of Add</p>
                <p className="mt-1 text-[20px] font-semibold text-[#211f26]">
                  {loadingHistory ? "Loading..." : creditTypeTotals.add}
                </p>
              </div>
              <div className="rounded-xl border border-[#eae7ec] bg-[#faf9fb] px-4 py-3">
                <p className="text-[12px] text-[#84828e]">Sum of Deduct</p>
                <p className="mt-1 text-[20px] font-semibold text-[#211f26]">
                  {loadingHistory ? "Loading..." : creditTypeTotals.deduct}
                </p>
              </div>
              <div className="rounded-xl border border-[#eae7ec] bg-[#faf9fb] px-4 py-3">
                <p className="text-[12px] text-[#84828e]">Sum of Return</p>
                <p className="mt-1 text-[20px] font-semibold text-[#211f26]">
                  {loadingHistory ? "Loading..." : creditTypeTotals.return}
                </p>
              </div>
            </div>
          </div>
        </div>

        {showCreditHistory && (
          <>
            <Table className="w-full">
              <TableHeader>
                <TableRow className="border-b-0">
                  <TableHead className="bg-[#faf9fb]">S.No.</TableHead>
                  <TableHead className="bg-[#faf9fb]">
                    Credit Block ID
                  </TableHead>
                  <TableHead className="bg-[#faf9fb]">Type</TableHead>
                  <TableHead className="bg-[#faf9fb]">Amount</TableHead>
                  <TableHead className="bg-[#faf9fb]">Remaining</TableHead>
                  <TableHead className="bg-[#faf9fb]">Amount Paid</TableHead>
                  <TableHead className="bg-[#faf9fb]">Ref Block ID</TableHead>
                  <TableHead className="bg-[#faf9fb]">Transaction ID</TableHead>
                  <TableHead className="bg-[#faf9fb]">
                    Transaction Group ID
                  </TableHead>
                  <TableHead className="bg-[#faf9fb]">Invoice ID</TableHead>
                  <TableHead className="bg-[#faf9fb]">Created At</TableHead>
                  <TableHead className="bg-[#faf9fb]">Expires At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {addHistory.map((item: any, index: number) => (
                  <TableRow key={item._id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{item._id}</TableCell>
                    <TableCell>{item.type || "-"}</TableCell>
                    <TableCell>{item.amount ?? "-"}</TableCell>
                    <TableCell>{addHistorySuffixSums[index]}</TableCell>
                    <TableCell>{item.amountPaid ?? "-"}</TableCell>
                    <TableCell>{item.refBlockId || "-"}</TableCell>
                    <TableCell>{item.transactionId || "-"}</TableCell>
                    <TableCell>{item.transactionGroupId || "-"}</TableCell>
                    <TableCell>{item.meta?.stripeInvoiceId || "-"}</TableCell>
                    <TableCell>{formatDateTime(item.createdAt)}</TableCell>
                    <TableCell>{formatDateTime(item.expiresAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {!loadingHistory && addHistory.length === 0 && (
              <div className="pt-4 text-[14px] text-[#84828e]">
                No add-credit history found for the selected range.
              </div>
            )}
          </>
        )}
      </div>
      <div className="rounded-2xl border border-[#eae7ec] bg-white p-5">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-[18px] font-semibold text-[#211f26]">
              Roll Over Breakdown
            </h2>
            <p className="text-[13px] text-[#84828e]">
              Derived from `available-filtered` breakdown data.
            </p>
          </div>

        </div>

        <Table className="w-full">
          <TableHeader>
            <TableRow className="border-b-0">
              <TableHead className="bg-[#faf9fb]">S.No.</TableHead>
              <TableHead className="bg-[#faf9fb]">Add Block ID</TableHead>
              <TableHead className="bg-[#faf9fb]">Added Amount</TableHead>
              <TableHead className="bg-[#faf9fb]">Deducted</TableHead>
              <TableHead className="bg-[#faf9fb]">Refunded</TableHead>
              <TableHead className="bg-[#faf9fb]">Net</TableHead>
              <TableHead className="bg-[#faf9fb]">Created At</TableHead>
              <TableHead className="bg-[#faf9fb]">Expires At</TableHead>
              <TableHead className="bg-[#faf9fb]">
                Deduction Rows Count
              </TableHead>
              <TableHead className="bg-[#faf9fb]">Refund Rows Count</TableHead>
              <TableHead className="bg-[#faf9fb]">Action</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {rollOverBreakdown.map((item: any, index: number) => {
              const isAlreadyApplied = appliedRollOverAddBlockIds.has(
                item.addBlockId,
              );

              return (
                <TableRow key={item.addBlockId}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{item.addBlockId}</TableCell>
                  <TableCell>{item.addedAmount ?? "-"}</TableCell>
                  <TableCell>{item.totalDeducted ?? "-"}</TableCell>
                  <TableCell>{item.totalRefunded ?? "-"}</TableCell>
                  <TableCell>{item.net ?? "-"}</TableCell>
                  <TableCell>{formatDateTime(item.createdAt)}</TableCell>
                  <TableCell>{formatDateTime(item.expiresAt)}</TableCell>
                  <TableCell>{item.deductions?.length ?? 0}</TableCell>
                  <TableCell>{item.refunds?.length ?? 0}</TableCell>
                  <TableCell>
                    <button
                      className={`rounded-lg border px-3 py-1.5 text-[12px] font-medium ${
                        isAlreadyApplied
                          ? "border-[#eae7ec] bg-[#eae7ec] text-[#84828e]"
                          : "border-[#eae7ec] bg-[#211f26] text-white hover:opacity-90"
                      }`}
                      onClick={() => {
                        if (isAlreadyApplied) {
                          notify(
                            "Rollover is already applied for this credit block.",
                            "error",
                          );
                        } else {
                          setSelectedRollOverItem(item);
                        }
                      }}
                    >
                      {isAlreadyApplied ? "Applied" : "Apply Roll Over"}
                    </button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {!loadingSummary && rollOverBreakdown.length === 0 && (
          <div className="pt-4 text-[14px] text-[#84828e]">
            No rollover breakdown found for this workspace.
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-[#eae7ec] bg-white p-5">
        <div className="mb-4">
          <h2 className="text-[18px] font-semibold text-[#211f26]">
            Rollover List
          </h2>
          <p className="text-[13px] text-[#84828e]">
            Previously applied rollovers for this workspace.
          </p>
        </div>

        <Table className="w-full">
          <TableHeader>
            <TableRow className="border-b-0">
              <TableHead className="bg-[#faf9fb]">S.No.</TableHead>
              <TableHead className="bg-[#faf9fb]">ID</TableHead>
              <TableHead className="bg-[#faf9fb]">Add Block ID</TableHead>
              <TableHead className="bg-[#faf9fb]">Rollover Amount</TableHead>
              <TableHead className="bg-[#faf9fb]">Triggered By</TableHead>
              <TableHead className="bg-[#faf9fb]">Previous Cycle</TableHead>
              <TableHead className="bg-[#faf9fb]">Current Cycle</TableHead>
              <TableHead className="bg-[#faf9fb]">Created At</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rollOverList.map((item: any, index: number) => (
              <TableRow key={item._id || index}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{item._id || "-"}</TableCell>
                <TableCell>{item.addBlockId || "-"}</TableCell>
                <TableCell>{item.rolloeverAmount ?? item.rolloverAmount ?? "-"}</TableCell>
                <TableCell>{item.triggeredByUserId || "-"}</TableCell>
                <TableCell>
                  {item.previousBillingCycle
                    ? `${formatDateTime(item.previousBillingCycle.start)} to ${formatDateTime(item.previousBillingCycle.end)}`
                    : "-"}
                </TableCell>
                <TableCell>
                  {item.currentBillingCycle
                    ? `${formatDateTime(item.currentBillingCycle.start)} to ${formatDateTime(item.currentBillingCycle.end)}`
                    : "-"}
                </TableCell>
                <TableCell>{formatDateTime(item.createdAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {!loadingRollOverList && rollOverList.length === 0 && (
          <div className="pt-4 text-[14px] text-[#84828e]">
            No rollovers applied yet for this workspace.
          </div>
        )}
        {loadingRollOverList && (
          <div className="pt-4 text-[14px] text-[#84828e]">Loading...</div>
        )}
      </div>

      <div className="rounded-2xl border border-[#eae7ec] bg-white p-5">
        <div className="mb-4">
          <h2 className="text-[18px] font-semibold text-[#211f26]">
            Preview Content
          </h2>
          <p className="text-[13px] text-[#84828e]">
            Content returned by `/:workspaceId/preview`.
          </p>
        </div>

        {loadingPreview && (
          <div className="text-[14px] text-[#84828e]">Loading preview...</div>
        )}

        {!loadingPreview && previewError && (
          <div className="text-[14px] text-[#d14343]">{previewError}</div>
        )}

        {!loadingPreview && !previewError && previewContent === null && (
          <div className="text-[14px] text-[#84828e]">
            No preview content available.
          </div>
        )}

        {!loadingPreview && !previewError && previewContent !== null && (
          <>
            <div className="mb-4 flex flex-wrap gap-4">
              <div className="rounded-xl border border-[#eae7ec] bg-[#faf9fb] px-4 py-3">
                <p className="text-[12px] text-[#84828e]">Created</p>
                <p className="mt-1 text-[20px] font-semibold text-[#211f26]">
                  {previewContent.created ?? 0}
                </p>
              </div>
              <div className="rounded-xl border border-[#eae7ec] bg-[#faf9fb] px-4 py-3">
                <p className="text-[12px] text-[#84828e]">Skipped</p>
                <p className="mt-1 text-[20px] font-semibold text-[#211f26]">
                  {previewContent.skipped ?? 0}
                </p>
              </div>
              <div className="rounded-xl border border-[#eae7ec] bg-[#faf9fb] px-4 py-3">
                <p className="text-[12px] text-[#84828e]">Errors</p>
                <p className="mt-1 text-[20px] font-semibold text-[#211f26]">
                  {previewContent.errors ?? 0}
                </p>
              </div>
            </div>

            <Table className="w-full">
              <TableHeader>
                <TableRow className="border-b-0">
                  <TableHead className="bg-[#faf9fb]">S.No.</TableHead>
                  <TableHead className="bg-[#faf9fb]">Add Block ID</TableHead>
                  <TableHead className="bg-[#faf9fb]">Status</TableHead>
                  <TableHead className="bg-[#faf9fb]">
                    Rollover Amount
                  </TableHead>
                  <TableHead className="bg-[#faf9fb]">Reason</TableHead>
                  <TableHead className="bg-[#faf9fb]">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {previewRows.map((item: any, index: number) => {
                  const isWouldCreate = item.status === "would_create";
                  const isAlreadyApplied = appliedRollOverAddBlockIds.has(
                    item.addBlockId,
                  );

                  return (
                    <TableRow key={`${item.addBlockId || "preview"}-${index}`}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{item.addBlockId || "-"}</TableCell>
                      <TableCell>{item.status || "-"}</TableCell>
                      <TableCell>
                        {item.rolloverAmount ?? item.rolloeverAmount ?? "-"}
                      </TableCell>
                      <TableCell>{item.reason || "-"}</TableCell>
                      <TableCell>
                        <button
                          className={`rounded-lg border px-3 py-1.5 text-[12px] font-medium ${
                            !isWouldCreate || isAlreadyApplied
                              ? "border-[#eae7ec] bg-[#eae7ec] text-[#84828e]"
                              : "border-[#eae7ec] bg-[#211f26] text-white hover:opacity-90"
                          }`}
                          onClick={() => {
                            if (!isWouldCreate) {
                              notify(
                                item.reason || "Rollover cannot be applied for this preview row.",
                                "error",
                              );
                              return;
                            }

                            if (isAlreadyApplied) {
                              notify(
                                "Rollover is already applied for this credit block.",
                                "error",
                              );
                              return;
                            }

                            setSelectedRollOverItem({
                              addBlockId: item.addBlockId,
                              net: item.rolloverAmount ?? item.rolloeverAmount ?? 0,
                            });
                          }}
                          disabled={!isWouldCreate || isAlreadyApplied}
                        >
                          {!isWouldCreate
                            ? "Unavailable"
                            : isAlreadyApplied
                              ? "Applied"
                              : "Apply Roll Over"}
                        </button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {previewRows.length === 0 && (
              <div className="pt-4 text-[14px] text-[#84828e]">
                No preview rows available.
              </div>
            )}
          </>
        )}
      </div>

      {selectedRollOverItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-lg rounded-xl border border-[#eae7ec] bg-white p-6 shadow-lg">
            <div className="mb-5">
              <h3 className="text-[18px] font-semibold text-[#211f26]">
                Apply Roll Over
              </h3>
              <p className="mt-1 text-[13px] text-[#84828e]">
                Confirm the rollover details before applying them.
              </p>
            </div>

            <div className="grid gap-3 text-[14px] text-[#211f26]">
              <div>
                <span className="font-medium">Add Block ID:</span>{" "}
                {selectedRollOverItem.addBlockId || "-"}
              </div>
              <div>
                <span className="font-medium">Rollover Amount:</span>{" "}
                {selectedRollOverItem.net ?? "-"}
              </div>
              <div>
                <span className="font-medium">Previous billing cycle:</span>{" "}
                {previousBillingCycleText || "-"}
              </div>
              <div>
                <span className="font-medium">Current billing cycle:</span>{" "}
                {currentBillingCycleText || "-"}
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                className="rounded-lg border border-[#eae7ec] px-4 py-2 text-[13px] text-[#211f26]"
                onClick={() => setSelectedRollOverItem(null)}
                disabled={applyingRollOver}
              >
                Cancel
              </button>
              <button
                className="rounded-lg bg-[#211f26] px-4 py-2 text-[13px] font-medium text-white disabled:opacity-60"
                onClick={handleApplyRollOver}
                disabled={applyingRollOver}
              >
                {applyingRollOver ? "Applying..." : "Confirm Apply"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
